'use client';

import clsx from 'clsx';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from 'ui/components/Carousel/Carousel';
import type { ModuleCardGrid } from '../../graphql/generated/types';
import { Media } from '../Media/Media';
import { useUserStore } from '../../stores/StoreProvider';
import { getCtaLink } from '../../utils/homepageLinks';
import Link from '@/components/Link';

const CardGrid = ({
  heading,
  topRowCardGroupCollection,
  bottomRowCardGroupCollection,
}: ModuleCardGrid): JSX.Element | null => {
  const strap = useUserStore((state) => state.strap);
  const allCards = [
    ...(topRowCardGroupCollection?.items ?? []),
    ...(bottomRowCardGroupCollection?.items ?? []),
  ];
  const filteredCards = allCards.filter((card) => {
    const link = getCtaLink(card?.cta);
    return link && (!strap || !link.includes('login'));
  });

  if (!filteredCards.length) return null;
  const totalCards = filteredCards.length;

  // Determine rows: single row if 3 or fewer cards, otherwise split into two rows
  let rows: (typeof filteredCards)[] = [[]];
  if (totalCards <= 3) {
    rows = [filteredCards];
  } else {
    const midIndex = Math.floor(totalCards / 2);
    rows = [filteredCards.slice(0, midIndex), filteredCards.slice(midIndex)];
  }

  return (
    <>
      {heading ? (
        <h2 className='m-0 pb-8 text-[28px] font-bold leading-[32px] sm:text-[36px] sm:font-semibold sm:leading-[40px] md:text-[48px] md:font-semibold md:leading-[52px] lg:text-[64px] lg:leading-[68px]'>
          {heading}
        </h2>
      ) : null}
      <div className='lg:hidden'>
        <Carousel>
          <CarouselContent>
            {filteredCards.map((deviceCard, index) => {
              if (!deviceCard) return null;
              const link = getCtaLink(deviceCard.cta);
              if (!link) return null;
              return (
                <CarouselItem
                  className='basis-[90%] sm:basis-[60%] md:basis-[40%]'
                  key={`${link}${index}`}
                >
                  <Link href={link}>
                    <div
                      className={clsx(
                        'relative h-full overflow-hidden rounded-lg text-black',
                        deviceCard.theme === 'dark' && 'text-white',
                        !deviceCard?.media &&
                          deviceCard.theme === 'dark' &&
                          'bg-black',
                        !deviceCard?.media &&
                          deviceCard.theme !== 'dark' &&
                          'bg-white',
                      )}
                    >
                      {deviceCard.media ? (
                        <Media
                          alt={deviceCard.media.description || ''}
                          className='block object-cover transition-transform duration-[8000ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]'
                          src={deviceCard.media.url || ''}
                        />
                      ) : null}
                      <div
                        className={clsx(
                          'absolute inset-0 z-10 flex flex-col gap-3 p-6',
                          deviceCard.alignment === 'bottom-left' &&
                            'bottom-0 top-auto items-start justify-end',
                          deviceCard.alignment === 'center' &&
                            'items-center justify-center text-center',
                        )}
                      >
                        <div className='flex flex-col gap-2'>
                          <h1 className='m-0 text-2xl font-bold normal-case leading-8 tracking-normal sm:text-[28px] sm:leading-8'>
                            {deviceCard.headingText}
                          </h1>
                          <h2 className='m-0 text-base font-medium normal-case leading-[22px] tracking-normal'>
                            {deviceCard.subtitleText}
                          </h2>
                        </div>
                        {deviceCard.cta ? (
                          <button className='w-[150px] rounded-xl bg-white px-8 py-3 text-center text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gray-200'>
                            {deviceCard.cta.text}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>

      <div className='hidden lg:mx-0 lg:flex lg:flex-col lg:gap-4 lg:overflow-visible lg:px-0'>
        {rows.map((cards, rowIndex) => (
          <div className='flex w-full flex-row gap-2' key={rowIndex}>
            {cards.map((deviceCard, index) => {
              if (!deviceCard) return null;
              const link = getCtaLink(deviceCard.cta);
              if (!link) return null;
              return (
                <Link className='flex-1' href={link} key={`${link}${index}`}>
                  <div
                    className={clsx(
                      'relative h-full w-full min-w-0 overflow-hidden rounded-lg text-black',
                      deviceCard.theme === 'dark' && 'text-white',
                      !deviceCard?.media &&
                        deviceCard.theme === 'dark' &&
                        'bg-black',
                      !deviceCard?.media &&
                        deviceCard.theme !== 'dark' &&
                        'bg-white',
                    )}
                  >
                    {deviceCard.media ? (
                      <Media
                        alt={deviceCard.media.description || ''}
                        className='block h-full w-full object-cover transition-transform duration-[8000ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]'
                        src={deviceCard.media.url || ''}
                      />
                    ) : null}
                    <div
                      className={clsx(
                        'absolute inset-0 z-10 flex flex-col gap-3 p-6',
                        deviceCard.alignment === 'bottom-left' &&
                          'bottom-0 top-auto items-start justify-end',
                        deviceCard.alignment === 'center' &&
                          'items-center justify-center text-center',
                      )}
                    >
                      <div className='flex flex-col gap-2'>
                        <h1 className='m-0 text-[28px] font-bold normal-case leading-8 tracking-normal'>
                          {deviceCard.headingText}
                        </h1>
                        <h2 className='m-0 text-base font-medium normal-case leading-[22px] tracking-normal'>
                          {deviceCard.subtitleText}
                        </h2>
                      </div>
                      {deviceCard.cta ? (
                        <button className='w-[150px] rounded-xl bg-white px-8 py-3 text-center text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gray-200'>
                          {deviceCard.cta.text}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
};

export default CardGrid;
